import React, { useRef, useState } from 'react';
import { ClickAwayListener, Popper } from '@material-ui/core';
import { useForm } from 'react-hook-form';
import moment from 'moment';

import FieldTitle from 'components/BookingForm/InsuranceBookingConfirmation/FieldTitle/FieldTitle';
import FormTextInput from 'components/BookingForm/FormTextInput/FormTextInput';
import NavigationBtnControl from 'components/BookingForm/NavigationBtnControl/NavigationBtnControl';
import { renderToast } from 'components/CustomToast/CustomToast';
import { updateBookingInsurance } from 'services/APIs';
import { loadOtpToken } from 'utils/storage';
import { useAppSelector } from 'redux/reduxHooks';
import { ReactComponent as DropdownIcon } from 'assets/icons/keyboard_arrow_down.svg';

import styles from 'components/BookingForm/InsuranceBookingConfirmation/InsuranceBookingConfirmation.module.scss';

interface IInsuranceInputFormValues {
  memberId: string;
  groupNumberId: string;
  subscriberDob: string;
  subscriberFullName: string;
  employerName: string;
  relationToSubscriber: string;
}

interface IInsuranceInputForm {
  bookingIds: string[];
  onDone: () => void;
}

const INSURANCE_RELATIONS = ['Self', 'Spouse', 'Parents/ Guardian', 'Other'];

const InsuranceInputForm = (props: IInsuranceInputForm) => {
  const { bookingIds, onDone } = props;

  const {
    control,
    setValue,
    watch,
    formState: { isValid },
    handleSubmit,
  } = useForm<IInsuranceInputFormValues>({
    mode: 'all',
    defaultValues: {
      memberId: '',
      groupNumberId: '',
      subscriberDob: '',
      subscriberFullName: '',
      employerName: '',
      relationToSubscriber: '',
    },
  });

  const {
    familyBookingSlice: {
      contactUser,
      step1: { phoneNumber },
    },
    clinicDetailsSlice: {
      clinicData: { isInsuranceRequired },
    },
    authSlice: { tempAccessToken },
  } = useAppSelector((state) => state);

  const buttonElementRef = useRef<HTMLButtonElement | null>(null);

  const [isOpen, setIsOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const { relationToSubscriber } = watch();

  const renderPlaceHolderText = (placeholder: string) =>
    isInsuranceRequired ? placeholder : `${placeholder} (optional)`;

  const populateCommonProps = () => {
    return {
      type: 'text',
      control,
      ...(isInsuranceRequired && {
        rules: { required: 'This field is required' },
      }),
    };
  };

  const onSelectPatientRelationToSubscriber = (relation: string) => {
    const self = INSURANCE_RELATIONS[0];

    if (relation === self) {
      setValue(
        'subscriberDob',
        contactUser.dob
          ? moment(contactUser.dob, 'YYYY-MM-DD').format('MM/DD/YYYY')
          : ''
      );

      setValue(
        'subscriberFullName',
        `${contactUser.firstName} ${contactUser.lastName}`.trim()
      );
    } else {
      setValue('subscriberDob', '');

      setValue('subscriberFullName', '');
    }

    setIsOpen(false);

    setValue('relationToSubscriber', relation);
  };

  const onSubmit = async (data: IInsuranceInputFormValues) => {
    const payload = {
      ...data,
      subscriberDob: data.subscriberDob
        ? moment(data.subscriberDob).format('YYYY-MM-DD')
        : '',
    };

    try {
      setIsLoading(true);

      await Promise.all(
        bookingIds.map((bookingId) =>
          updateBookingInsurance(
            { ...payload, bookingId },
            {
              otpToken: loadOtpToken(phoneNumber),
              tmpAccessToken: tempAccessToken,
            }
          )
        )
      );

      onDone();
    } catch (error) {
      renderToast({
        message:
          'We could not save your insurance details. Please try again later',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className={styles['group-container']}>
        <div>
          <FieldTitle>Group ID</FieldTitle>

          <FormTextInput
            name="groupNumberId"
            placeholder={renderPlaceHolderText('Input group ID')}
            {...populateCommonProps()}
          />
        </div>

        <div>
          <FieldTitle>Member ID</FieldTitle>

          <FormTextInput
            name="memberId"
            placeholder={renderPlaceHolderText('Input member ID')}
            {...populateCommonProps()}
          />
        </div>
      </div>

      <ClickAwayListener onClickAway={() => setIsOpen(false)}>
        <div className={styles['dropdown-container']}>
          <FieldTitle>Patient’s Relation to Subscriber</FieldTitle>

          <button
            ref={buttonElementRef}
            className={`${styles['button']} ${
              relationToSubscriber ? styles['button-selected'] : ''
            }`}
            onClick={() => setIsOpen(!isOpen)}
          >
            <span>
              {relationToSubscriber
                ? relationToSubscriber
                : renderPlaceHolderText('Select')}
            </span>

            <DropdownIcon />
          </button>

          <Popper
            open={isOpen}
            anchorEl={buttonElementRef.current}
            placement="bottom-start"
            style={{ zIndex: 9999 }}
          >
            <div
              className={styles['dropdown']}
              style={{
                width: isOpen ? buttonElementRef.current?.clientWidth : '100%',
              }}
            >
              {INSURANCE_RELATIONS.map((relation) => (
                <button
                  key={relation}
                  onClick={() => onSelectPatientRelationToSubscriber(relation)}
                  data-active={relationToSubscriber === relation}
                >
                  {relation}
                </button>
              ))}
            </div>
          </Popper>
        </div>
      </ClickAwayListener>

      <div className={styles['group-container']}>
        <div>
          <FieldTitle>Subscriber’s Full Name</FieldTitle>

          <FormTextInput
            name="subscriberFullName"
            placeholder={renderPlaceHolderText('Input full name')}
            {...populateCommonProps()}
          />
        </div>

        <div>
          <FieldTitle>Subscriber’s Date of Birth</FieldTitle>

          <FormTextInput
            type="date"
            name="subscriberDob"
            label="Date of Birth"
            control={control}
            placeholder={renderPlaceHolderText('MM/DD/YYYY')}
            mask="99/99/9999"
            rules={{
              validate: (value: string) => {
                if (!value) return true;

                const date = moment(value, 'MM/DD/YYYY', true);

                if (!date.isValid()) {
                  return 'Invalid date of birth format';
                }
              },
            }}
          />
        </div>
      </div>

      <FieldTitle>Employer Name</FieldTitle>

      <FormTextInput
        name="employerName"
        placeholder={renderPlaceHolderText('Input employer name')}
        {...populateCommonProps()}
      />

      <NavigationBtnControl
        nextBtnTitle="Done"
        onNext={handleSubmit(onSubmit)}
        isDisabledNextBtn={!isValid}
        isLoading={isLoading}
      />
    </>
  );
};

export default InsuranceInputForm;
